import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../lib/auth';
import { useSuccessAlert, useErrorAlert } from './AlertSystem';
import './AuthPage.css';

interface FormData {
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  password?: string;
  confirmPassword?: string;
  general?: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

const ResetPasswordPage: React.FC = () => {
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  
  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check if token is present
  useEffect(() => {
    if (!resetToken) {
      showError('Invalid or missing reset token');
    }
  }, [resetToken, showError]);

  // Password strength calculation
  const getPasswordStrength = (password: string): PasswordStrength => {
    let score = 0;
    
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    const strengths = [
      { score: 0, label: 'Very Weak', color: '#ef4444' },
      { score: 1, label: 'Weak', color: '#f97316' },
      { score: 2, label: 'Fair', color: '#eab308' },
      { score: 3, label: 'Good', color: '#22c55e' },
      { score: 4, label: 'Strong', color: '#16a34a' },
      { score: 5, label: 'Very Strong', color: '#15803d' }
    ];

    return strengths[score];
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength.score < 3) {
      newErrors.password = 'Password is too weak. Please choose a stronger password';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetToken) {
      showError('Invalid or missing reset token');
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      await authService.resetPassword(resetToken, formData.password);
      showSuccess('Password reset successful! Redirecting to login...');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Reset password error:', error);
      showError(error instanceof Error ? error.message : 'Password reset failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background" />
      
      <div className="container">
        <div className="auth-container">
          
          {/* Auth Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <div className="auth-logo-icon">
                <i className="ri-shield-keyhole-line"></i>
              </div>
              <span className="auth-logo-text">Nuroveli</span>
            </div>
            
            <h1 className="auth-title">Reset Password</h1>
            <p className="auth-subtitle">
              Enter your new password to complete the reset process
            </p>
          </div>

          {/* Reset Password Form */}
          <form className="auth-form" onSubmit={handleSubmit}>
            
            {/* General Error */}
            {errors.general && (
              <div className="auth-error">
                <i className="ri-error-warning-line"></i>
                <span>{errors.general}</span>
              </div>
            )}

            {/* Password Field */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="password">
                New Password
              </label>
              <div className="auth-input-wrapper">
                <div className="auth-input-icon">
                  <i className="ri-lock-line"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className={`auth-input ${errors.password ? 'auth-input-error' : ''}`}
                  placeholder="Enter your new password"
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  autoFocus
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="auth-password-strength">
                  <div className="auth-strength-bar">
                    <div 
                      className="auth-strength-fill"
                      style={{ 
                        width: `${(passwordStrength.score / 5) * 100}%`,
                        backgroundColor: passwordStrength.color
                      }}
                    />
                  </div>
                  <span 
                    className="auth-strength-label"
                    style={{ color: passwordStrength.color }}
                  >
                    {passwordStrength.label}
                  </span>
                </div>
              )}
              
              {errors.password && (
                <span className="auth-field-error">{errors.password}</span>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="confirmPassword">
                Confirm New Password
              </label>
              <div className="auth-input-wrapper">
                <div className="auth-input-icon">
                  <i className="ri-lock-line"></i>
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  className={`auth-input ${errors.confirmPassword ? 'auth-input-error' : ''}`}
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="auth-field-error">{errors.confirmPassword}</span>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={isLoading || !resetToken}
            >
              <span className="auth-btn-text">
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </span>
              <div className="auth-btn-icon">
                <i className={isLoading ? 'ri-loader-4-line auth-spinner' : 'ri-check-line'}></i>
              </div>
              <div className="auth-btn-glow"></div>
            </button>

            {/* Back to Login */}
            <div className="auth-form-footer">
              <Link to="/login" className="auth-link">
                <i className="ri-arrow-left-line"></i>
                Back to Login
              </Link>
            </div>

          </form>

          {/* Auth Footer */}
          <div className="auth-footer">
            <p className="auth-footer-text">
              Remembered your password? 
              <Link to="/login" className="auth-footer-link">
                Sign in here
              </Link>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
